import { useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useAdminEdgeFunction } from "@/hooks/useAdminEdgeFunction";
import { supabase } from "@/integrations/supabase/client";

interface CreateRolloutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const CreateRolloutDialog = ({
  open,
  onOpenChange,
  onSuccess,
}: CreateRolloutDialogProps) => {
  const { invokeAdmin, isLoading } = useAdminEdgeFunction();
  const [formData, setFormData] = useState({
    version_id: "",
    rollout_type: "gradual" as "gradual" | "ab_test",
    traffic_percentage: 10,
    ring: "internal" as "internal" | "beta" | "production",
  });

  const { data: versions } = useQuery({
    queryKey: ["prompt-versions-for-rollout"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prompt_versions" as any)
        .select(`
          id,
          version_number,
          prompt_templates:template_id (name)
        `)
        .eq("is_active", true);

      if (error) throw error;
      return data;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { error } = await invokeAdmin("create-prompt-rollout", {
      body: formData,
      showErrorToast: true,
    });

    if (!error) {
      onSuccess();
      setFormData({
        version_id: "",
        rollout_type: "gradual",
        traffic_percentage: 10,
        ring: "internal",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create Rollout</DialogTitle>
          <DialogDescription>
            Set up a gradual rollout or A/B test for a prompt version
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="version">Version</Label>
            <Select
              value={formData.version_id}
              onValueChange={(value) => setFormData({ ...formData, version_id: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a version" />
              </SelectTrigger>
              <SelectContent>
                {versions?.map((version: any) => (
                  <SelectItem key={version.id} value={version.id}>
                    {version.prompt_templates?.name} v{version.version_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Rollout Type</Label>
            <RadioGroup
              value={formData.rollout_type}
              onValueChange={(value: any) =>
                setFormData({ ...formData, rollout_type: value })
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="gradual" id="gradual" />
                <Label htmlFor="gradual" className="font-normal">
                  Gradual Rollout (increase traffic over time)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="ab_test" id="ab_test" />
                <Label htmlFor="ab_test" className="font-normal">
                  A/B Test (fixed traffic split)
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="traffic">Initial Traffic Percentage</Label>
            <Input
              id="traffic"
              type="number"
              min="1"
              max="100"
              value={formData.traffic_percentage}
              onChange={(e) =>
                setFormData({ ...formData, traffic_percentage: parseInt(e.target.value) })
              }
              required
            />
            <p className="text-sm text-muted-foreground">
              Percentage of traffic that will use the new version
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ring">Deployment Ring</Label>
            <Select
              value={formData.ring}
              onValueChange={(value: any) => setFormData({ ...formData, ring: value })}
              required
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">Internal (team only)</SelectItem>
                <SelectItem value="beta">Beta (selected users)</SelectItem>
                <SelectItem value="production">Production (all users)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Creating..." : "Create Rollout"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
